// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * cm-coding: Managers need to know how a knowledge-base item becomes prompt
 * guidance and then cited findings before they can curate useful content. Show
 * that three-step workflow in language specific to the selected mode. Persist
 * the collapsed state only in this browser because the strip is personal
 * orientation, not shared workspace status, and experienced users should be
 * able to keep it out of the way.
 */
import { useState } from 'react'
import { ChevronDown, Lightbulb } from 'lucide-react'

import type { KnowledgeBaseMode } from '../types'

const STORAGE_KEY = 'kb-how-it-works-collapsed'

export const MODE_CHIP: Record<KnowledgeBaseMode, string> = {
  findings: 'Findings · scored 1–5',
  criteria: 'Criteria',
  report: 'Report sections',
}

const STEPS: Record<KnowledgeBaseMode, { title: string; steps: [string, string, string] }> = {
  findings: {
    title: 'How this knowledge base works',
    steps: [
      'Write guidance items describing what to look for in documents — one clause, risk, or rule per item, each with a risk level from 1 (minor) to 5 (critical).',
      'Every analysis run automatically includes these items in the AI\'s instructions. You never trigger this — it happens whenever documents are analyzed in this workspace.',
      'When the AI finds a match, the finding cites the item\'s ID, and the document\'s risk score comes from the highest-risk item cited — your risk levels, not the AI\'s judgment.',
    ],
  },
  criteria: {
    title: 'How this knowledge base works',
    steps: [
      'Write guidance items describing the areas the analysis should pay attention to — one topic or question per item.',
      'Every analysis run automatically includes these items in the AI\'s instructions. You never trigger this — it happens whenever documents are analyzed in this workspace.',
      'The AI organizes what it finds around your criteria, so its output follows the areas you defined.',
    ],
  },
  report: {
    title: 'How this knowledge base works',
    steps: [
      'Write one item per report section, describing what that section should cover and how it should be written.',
      'When a report is generated, the AI receives your section frameworks as its outline.',
      'The finished report follows your sections — add, edit, or remove items here to change the report\'s structure.',
    ],
  },
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true'
  } catch {
    return false
  }
}

export function KbHowItWorks({ mode }: { mode: KnowledgeBaseMode }): JSX.Element {
  const [collapsed, setCollapsed] = useState(readCollapsed)
  const { title, steps } = STEPS[mode]

  const toggle = () => {
    setCollapsed(prev => {
      try {
        localStorage.setItem(STORAGE_KEY, String(!prev))
      } catch {
        // Collapse preference is cosmetic — losing it is fine.
      }
      return !prev
    })
  }

  return (
    <div className="rounded-xl border border-[#dbeafe] bg-[#eff6ff]" data-testid="kb-how-it-works">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={!collapsed}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
      >
        <Lightbulb className="h-4 w-4 flex-none text-[#2563eb]" />
        <span className="flex-1 text-[13px] font-semibold text-[#1e40af]">{title}</span>
        <ChevronDown
          className={`h-4 w-4 flex-none text-[#60a5fa] transition-transform duration-150 ${collapsed ? '-rotate-90' : ''}`}
        />
      </button>
      {!collapsed && (
        <ol className="grid gap-3 px-4 pb-4 sm:grid-cols-3">
          {steps.map((step, i) => (
            <li key={i} className="flex gap-2.5">
              <span className="mt-px flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[#2563eb] text-[11px] font-semibold text-white">
                {i + 1}
              </span>
              <span className="text-[12.5px] leading-[1.55] text-[#1e3a8a]">{step}</span>
            </li>
          ))}
        </ol>
      )}
    </div>
  )
}
